import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
  memo,
} from "react";
import {
  Box,
  useTheme,
  makeStyles,
  Button as MuiButton,
  Tooltip,
} from "@material-ui/core";
import { Warning as WarningIcon } from "@material-ui/icons";
import { ListContextProvider } from "react-admin";
import { VariableSizeList as List, areEqual } from "react-window";
import SelectionDialog from "../../../../components/common/ra-customizable-datagrid/SelectionDialog";
import LocalStorage from "../../../../components/common/ra-customizable-datagrid/LocalStorage";
import {
  SearchIcon,
  CustomFieldsIcon,
  InboundIcon,
  OutboundIcon,
} from "../../../../components/common/iconography";
import clsx from "clsx";
import { useTrackContext } from "../TrackContext";
import {
  getAccountColor,
  getProductColor,
  getProductName,
  getTrackCarDisplayFields,
  isRestrictedForCustomer,
} from "../../../../utils/trackListUtils";
import { getAisleFullName } from "../../../../utils/locationUtils";
import { isOutbound } from "../../../../utils/trackCarPersistence";
import { differenceInDays } from "date-fns";
import {
  TRACK_LIST_STATUS_ACTIVE_BG,
  TRACK_LIST_STATUS_ACTIVE_BORDER,
  TRACK_LIST_STATUS_ACTIVE_FG,
  TRACK_LIST_STATUS_RELEASED_BG,
  TRACK_LIST_STATUS_RELEASED_BORDER,
  TRACK_LIST_STATUS_RELEASED_FG,
  TRACK_LIST_TYPE_INBOUND_BG,
  TRACK_LIST_TYPE_INBOUND_FG,
  TRACK_LIST_TYPE_OUTBOUND_BG,
  TRACK_LIST_TYPE_OUTBOUND_FG,
} from "../../../../utils/trackManagementPalette";

const COLUMN_STORAGE_KEY = "TrackManagementList";
const DEFAULT_COLUMNS = [
  "trackName",
  "position",
  "railCarNumber",
  "type",
  "carType",
  "carLength",
  "accountName",
  "productName",
  "status",
  "poNumber",
  "age",
];

const DEFAULT_AGE_META = {
  text: "N/A",
  color: "#757575",
  fontWeight: 400,
};

const getAgeMetadata = (record) => {
  const isOutboundRecord = isOutbound(record);
  const connectedShipment = record?.inbound ?? record?.outbound ?? null;
  // Age on track runs from placement (spotting); arrivalDateTime is the
  // legacy home of the placed time and the gate-arrival stamp going forward.
  const dateReceived = isOutboundRecord
    ? connectedShipment?.placementDateTime ?? connectedShipment?.arrivalDateTime
    : connectedShipment?.endDateTime;

  if (!dateReceived) {
    return DEFAULT_AGE_META;
  }

  const daysDiff = differenceInDays(new Date(), new Date(dateReceived));

  if (daysDiff < 0) {
    return { text: "Future", color: "#757575", fontWeight: 400 };
  }

  if (daysDiff === 0) {
    return { text: "Today", color: "#757575", fontWeight: 400 };
  }

  let color = "#757575";
  if (daysDiff > 30) {
    color = "#d32f2f";
  } else if (daysDiff > 7) {
    color = "#f57c00";
  }

  return {
    text: `${daysDiff} day${daysDiff !== 1 ? "s" : ""}`,
    color,
    fontWeight: daysDiff > 7 ? 500 : 400,
  };
};

const useStyles = makeStyles((theme) => ({
  listViewContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  columnControls: {
    display: "flex",
    justifyContent: "flex-end",
    padding: theme.spacing(0.5, 1),
  },
  virtualTable: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  headerRow: {
    display: "grid",
    gap: theme.spacing(0.5),
    padding: (props) => (props.compactMode ? "6px 8px" : "12px 16px"),
    borderBottom: `2px solid ${theme.palette.divider}`,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.05)"
        : "rgba(0,0,0,0.03)",
    fontSize: (props) => (props.compactMode ? "0.68rem" : "0.75rem"),
    color: theme.palette.text.secondary,
  },
  headerCell: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  virtualListWrapper: {
    width: "100%",
  },
  virtualRow: {
    display: "grid",
    gap: theme.spacing(0.5),
    padding: (props) => (props.compactMode ? "4px 8px" : "8px 16px"),
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: "pointer",
    transition: "background-color 80ms ease",
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.04)"
          : "rgba(0,0,0,0.04)",
    },
  },
  virtualCell: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: (props) => (props.compactMode ? "0.75rem" : "0.875rem"),
  },
  highlightRow: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 214, 0, 0.12)"
        : "rgba(255, 235, 59, 0.2)",
    boxShadow: `inset 0 0 0 1px ${theme.palette.warning.main}`,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: "12px",
    fontWeight: 500,
    fontSize: "0.75rem",
    border: "1px solid transparent",
  },
  typeTag: {
    padding: "2px 6px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontWeight: 500,
  },
  trackHeaderBox: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: "2px",
  },
  progressRoot: {
    height: 4,
    borderRadius: 2,
    backgroundColor:
      theme.palette.grey[theme.palette.type === "light" ? 200 : 700],
    overflow: "hidden",
    width: "100%",
    position: "relative",
  },
  progressBar: {
    height: "100%",
    backgroundColor: theme.palette.primary.main,
    transition: "width 0.4s ease",
  },
}));

export const TrackListView = () => {
  const {
    processedCars: visibleCars = [],
    tracks = [],
    processedCarsMap = {},
    compactMode = false,
    equipmentTypeMap = {},
    foundCarMap = {},
    staleTracks = {},
    colorsLoaded = 0,
    openShipment,
    readonly,
    isCustomer,
    customerAccountId,
  } = useTrackContext();

  const theme = useTheme();
  const classes = useStyles({ compactMode });
  const listRef = useRef();

  const [columnsModalOpen, setColumnsModalOpen] = useState(false);
  const [columnSelection, setColumnSelection] = useState(() => {
    if (typeof window === "undefined") return {};
    return (
      LocalStorage.get(COLUMN_STORAGE_KEY) ||
      DEFAULT_COLUMNS.reduce((acc, k) => ({ ...acc, [k]: true }), {})
    );
  });

  useEffect(() => {
    LocalStorage.set(COLUMN_STORAGE_KEY, columnSelection);
  }, [columnSelection]);

  const handleToggleColumn = useCallback((col) => {
    setColumnSelection((prev) => {
      const next = { ...prev, [col]: !prev[col] };
      return Object.values(next).some(Boolean) ? next : prev;
    });
  }, []);

  const [limit, setLimit] = useState(100);
  const handleShowMore = useCallback(() => setLimit((p) => p + 100), []);

  const { dataMap, allIds, trackByLocationId } = useMemo(() => {
    if (!tracks.length) return { dataMap: {}, allIds: [], trackByLocationId: new Map() };
    const sorted = [];
    const map = {};
    const visibleCarsSet = new Set(visibleCars.map((c) => c.id));

    // Create a fast lookup for track objects
    const trackMap = new Map();
    tracks.forEach((t) => {
      const tid = t.locations?.items?.[0]?.id;
      if (tid) trackMap.set(tid, t);
    });

    // Iterate through tracks and their cars once
    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];
      const trackId = track.locations?.items?.[0]?.id;
      if (!trackId) continue;

      const carsOnTrack = processedCarsMap[trackId] || [];
      let isFirstInThisTrack = true;

      for (let j = 0; j < carsOnTrack.length; j++) {
        const car = carsOnTrack[j];
        if (visibleCarsSet.has(car.id)) {
          // Minimal decoration for identification, rest moved to render time
          const record = {
            ...car,
            _trackId: trackId,
            _isFirstInTrack: isFirstInThisTrack,
            _position: j + 1,
            _isStale: !!staleTracks[trackId],
            _isSearchMatch: !!foundCarMap[car.id],
          };
          sorted.push(record);
          map[car.id] = record;
          if (isFirstInThisTrack) isFirstInThisTrack = false;
        }
      }
    }
    return { dataMap: map, allIds: sorted.map((c) => c.id), trackByLocationId: trackMap };
  }, [visibleCars, processedCarsMap, tracks, staleTracks, foundCarMap]);

  const totalCount = allIds.length;
  const slicedIds = useMemo(() => allIds.slice(0, limit), [allIds, limit]);
  const slicedDataMap = useMemo(() => {
    const m = {};
    slicedIds.forEach((id) => {
      if (dataMap[id]) m[id] = dataMap[id];
    });
    return m;
  }, [dataMap, slicedIds]);

  const visibleRecords = useMemo(
    () => slicedIds.map((id) => dataMap[id]).filter(Boolean),
    [slicedIds, dataMap],
  );

  useEffect(() => {
    if (listRef.current) listRef.current.resetAfterIndex(0);
  }, [visibleRecords, compactMode]);

  const listContext = useMemo(
    () => ({
      data: slicedDataMap,
      ids: slicedIds,
      total: totalCount,
      loaded: true,
      loading: false,
      page: 1,
      perPage: limit,
      setPage: () => {},
      setPerPage: () => {},
      currentSort: { field: "trackName", order: "ASC" },
      resource: "tracks",
      basePath: "/tracks",
    }),
    [slicedDataMap, slicedIds, totalCount, limit],
  );

  const columnDefs = useMemo(
    () => [
      {
        key: "trackName",
        label: "Track",
        minWidth: 200,
        flex: 1.5,
        render: (r) => (
          <div className={classes.trackHeaderBox}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                minWidth: 0,
                flexShrink: 1,
                overflow: "hidden",
              }}
            >
              <Tooltip title={r._trackName} arrow>
                <span
                  style={{
                    fontWeight: 600,
                    fontFamily: "monospace",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                  }}
                >
                  {r._trackName}
                </span>
              </Tooltip>
              {r._isFirstInTrack && r._isStale && (
                <WarningIcon
                  style={{ color: "#f57c00", fontSize: 14, flexShrink: 0 }}
                />
              )}
            </div>
            {r._isFirstInTrack && r._capacityUsed != null && (
              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <div className={classes.progressRoot} style={{ maxWidth: 100 }}>
                  <div
                    className={classes.progressBar}
                    style={{ width: `${r._capacityUsed}%` }}
                  />
                </div>
                <span style={{ fontSize: "0.65rem", opacity: 0.8 }}>
                  {r._capacityUsed}%
                </span>
              </div>
            )}
          </div>
        ),
      },
      {
        key: "position",
        label: "Pos",
        width: 50,
        align: "center",
        render: (r) => (
          <div
            style={{
              backgroundColor: r._position ? "#e3f2fd" : "#f5f5f5",
              color: r._position ? "#1976d2" : "#757575",
              borderRadius: "50%",
              width: 24,
              height: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 600,
              fontSize: "0.7rem",
            }}
          >
            {r._position || "—"}
          </div>
        ),
      },
      {
        key: "railCarNumber",
        label: "Railcar #",
        minWidth: 120,
        flex: 1,
        render: (r) => (
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <span
              style={{
                fontWeight: 600,
                fontFamily: "monospace",
                color: r._isSearchMatch
                  ? theme.palette.type === "dark"
                    ? "#ffeb3b"
                    : "#e65100"
                  : "inherit",
              }}
            >
              {r.railCarNumber || "N/A"}
            </span>
            {r._isSearchMatch && (
              <SearchIcon
                style={{ color: theme.palette.warning.main, fontSize: 14 }}
              />
            )}
          </div>
        ),
      },
      {
        key: "type",
        label: "Type",
        width: 100,
        render: (r) => {
          if (r._isRestricted) {
            return <span>—</span>;
          }
          const isOut = isOutbound(r);
          return (
            <div
              className={classes.typeTag}
              style={{
                backgroundColor: isOut
                  ? TRACK_LIST_TYPE_OUTBOUND_BG
                  : TRACK_LIST_TYPE_INBOUND_BG,
                color: isOut
                  ? TRACK_LIST_TYPE_OUTBOUND_FG
                  : TRACK_LIST_TYPE_INBOUND_FG,
              }}
            >
              {isOut ? (
                <OutboundIcon style={{ fontSize: 14 }} />
              ) : (
                <InboundIcon style={{ fontSize: 14 }} />
              )}
              <span style={{ fontSize: "0.7rem" }}>{isOut ? "OUT" : "IN"}</span>
            </div>
          );
        },
      },
      {
        key: "status",
        label: "Status",
        width: 110,
        render: (r) =>
          r._isRestricted ? (
            <span>—</span>
          ) : (
            <div
              className={classes.chip}
              style={{
                backgroundColor:
                  r.status === "RELEASED"
                    ? TRACK_LIST_STATUS_RELEASED_BG
                    : TRACK_LIST_STATUS_ACTIVE_BG,
                color:
                  r.status === "RELEASED"
                    ? TRACK_LIST_STATUS_RELEASED_FG
                    : TRACK_LIST_STATUS_ACTIVE_FG,
                border: `1px solid ${
                  r.status === "RELEASED"
                    ? TRACK_LIST_STATUS_RELEASED_BORDER
                    : TRACK_LIST_STATUS_ACTIVE_BORDER
                }`,
              }}
            >
              {r.status || "ACTIVE"}
            </div>
          ),
      },
      {
        key: "accountName",
        label: "Account",
        minWidth: 150,
        flex: 1.2,
        render: (r) =>
          r._isRestricted ? (
            <span>—</span>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "2px",
                  backgroundColor: r._accountColor || "#e0e0e0",
                }}
              />
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {r.accountName || "N/A"}
              </span>
            </div>
          ),
      },
      {
        key: "productName",
        label: "Product",
        minWidth: 150,
        flex: 1.2,
        render: (r) =>
          r._isRestricted ? (
            <span>—</span>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "2px",
                  backgroundColor: r._productColor || "#e0e0e0",
                }}
              />
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {r.productName ||
                  getProductName(r.productId) ||
                  "N/A"}
              </span>
            </div>
          ),
      },
      {
        key: "carType",
        label: "Car Type",
        width: 90,
        render: (r) => (
          <span>
            {r._isRestricted
              ? "—"
              : equipmentTypeMap[r.carrierEquipmentTypeId]?.name ||
                r.railEquipmentType?.name ||
                "N/A"}
          </span>
        ),
      },
      {
        key: "carLength",
        label: "Length",
        width: 70,
        render: (r) => {
          if (r._isRestricted) return <span>—</span>;
          const length =
            equipmentTypeMap[r.carrierEquipmentTypeId]?.length ||
            r.railEquipmentType?.length;
          return <span>{length != null ? `${length} ft` : "N/A"}</span>;
        },
      },
      {
        key: "poNumber",
        label: "PO #",
        minWidth: 100,
        flex: 0.8,
        render: (r) => (
          <span style={{ fontFamily: "monospace" }}>
            {r._isRestricted ? "—" : r.poNumber || "N/A"}
          </span>
        ),
      },
      {
        key: "age",
        label: "Age",
        width: 80,
        render: (r) => (
          <span
            style={{
              color: r._isRestricted ? "#757575" : r._ageColor,
              fontWeight: r._isRestricted ? 400 : r._ageFontWeight,
            }}
          >
            {r._isRestricted ? "—" : r._ageText}
          </span>
        ),
      },
    ],
    [classes, equipmentTypeMap, theme],
  );

  const visibleColumnsList = useMemo(
    () => columnDefs.filter((c) => columnSelection[c.key] !== false),
    [columnDefs, columnSelection],
  );
  const gridTemplateColumns = useMemo(
    () =>
      visibleColumnsList
        .map((c) =>
          c.width
            ? `${c.width}px`
            : `minmax(${c.minWidth || 80}px, ${c.flex || 1}fr)`,
        )
        .join(" "),
    [visibleColumnsList],
  );

  const rowHeight = compactMode ? 54 : 68;
  const getItemSize = useCallback(
    (idx) => {
      const r = visibleRecords[idx];
      if (!r) return rowHeight;
      return r._isFirstInTrack ? (compactMode ? 74 : 88) : rowHeight;
    },
    [visibleRecords, compactMode, rowHeight],
  );

  const handleRowClick = useCallback(
    (id, rec) => {
      if (readonly) return;
      openShipment(rec);
    },
    [openShipment, readonly],
  );

  const virtualizedData = useMemo(
    () => ({
      records: visibleRecords,
      columns: visibleColumnsList,
      gridTemplateColumns,
      onRowClick: handleRowClick,
      classes,
      theme,
      readonly,
      trackByLocationId,
      // Busts react-window itemData when module-level color maps refresh (not read per row).
      _colorMapRevision: colorsLoaded,
      isCustomer,
      customerAccountId,
    }),
    [
      visibleRecords,
      visibleColumnsList,
      gridTemplateColumns,
      handleRowClick,
      classes,
      theme,
      readonly,
      trackByLocationId,
      colorsLoaded,
      isCustomer,
      customerAccountId,
    ],
  );

  return (
    <Box p={compactMode ? 0.5 : 2} className={classes.listViewContainer}>
      <ListContextProvider value={listContext}>
        <div className={classes.columnControls}>
          <MuiButton
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<CustomFieldsIcon />}
            onClick={() => setColumnsModalOpen(true)}
          >
            Columns
          </MuiButton>
        </div>
        {columnsModalOpen && (
          <SelectionDialog
            selection={columnSelection}
            columns={columnDefs.map((c) => ({ source: c.key, label: c.label }))}
            onColumnClicked={handleToggleColumn}
            onClose={() => setColumnsModalOpen(false)}
          />
        )}
        <div className={classes.virtualTable}>
          <div className={classes.headerRow} style={{ gridTemplateColumns }}>
            {visibleColumnsList.map((c) => (
              <div key={c.key} className={classes.headerCell}>
                {c.label}
              </div>
            ))}
          </div>
          <div className={classes.virtualListWrapper}>
            <List
              ref={listRef}
              height={600}
              itemCount={visibleRecords.length}
              itemSize={getItemSize}
              width="100%"
              itemData={virtualizedData}
              itemKey={(idx, d) => d.records[idx]?.id ?? idx}
              overscanCount={15}
            >
              {VirtualizedRow}
            </List>
          </div>
        </div>
        {totalCount > limit && (
          <Box display="flex" justifyContent="center" mt={2} mb={2}>
            <MuiButton
              variant="contained"
              color="primary"
              onClick={handleShowMore}
              style={{ minWidth: 200, fontWeight: 600 }}
            >
              Show More ({totalCount - limit} remaining)
            </MuiButton>
          </Box>
        )}
      </ListContextProvider>
    </Box>
  );
};

const VirtualizedRow = memo(({ index, style, data }) => {
  const record = data.records[index];
  if (!record) return null;

  // Decorate record at render time
  const track = data.trackByLocationId.get(record._trackId);
  const trackLocation = track?.locations?.items?.[0];
  const ageMeta = getAgeMetadata(record);
  const isRestricted = isRestrictedForCustomer(
    record,
    data.isCustomer,
    data.customerAccountId,
  );

  const displayFields = getTrackCarDisplayFields(record);
  const connectedShipment = displayFields.shipment || {};
  const status =
    record?.inbound?.status ?? record?.outbound?.status ?? null;
  const decoratedRecord = {
    ...record,
    status,
    accountName: displayFields.accountName,
    accountId: displayFields.accountId,
    railCarNumber: displayFields.railCarNumber,
    productName: displayFields.productName,
    productId: displayFields.productId,
    productColor: displayFields.productColor,
    reloadYardCommodityId: displayFields.reloadYardCommodityId,
    poNumber: connectedShipment.poNumber ?? null,
    carrierEquipmentTypeId: connectedShipment.carrierEquipmentTypeId ?? null,
    railEquipmentType: connectedShipment.railEquipmentType ?? null,
    _trackName: getAisleFullName(track) || track?.name || "N/A",
    _capacityUsed:
      typeof trackLocation?.capacityUsed === "number"
        ? trackLocation.capacityUsed
        : null,
    _accountColor: isRestricted
      ? null
      : getAccountColor(displayFields.accountId),
    // Product color is productId-based; no commodity fallback in track views.
    _productColor: isRestricted
      ? null
      : displayFields.productColor ||
        getProductColor(displayFields.productId),
    _ageText: ageMeta.text,
    _ageColor: ageMeta.color,
    _ageFontWeight: ageMeta.fontWeight,
    _isRestricted: isRestricted,
  };

  const borderWidth = record._isFirstInTrack ? 3 : 0;
  const isSearch = record._isSearchMatch;

  const virtualRowClass = clsx(
    data.classes.virtualRow,
    isSearch && data.classes.highlightRow,
  );

  const handleRowClickLocal = () => {
    if (isRestricted) return;
    data.onRowClick(record.id, record);
  };

  return (
    <div
      style={{
        ...style,
        width: "100%",
        cursor: data.readonly || isRestricted ? "default" : "pointer",
      }}
      onClick={handleRowClickLocal}
    >
      <div
        className={virtualRowClass}
        style={{
          gridTemplateColumns: data.gridTemplateColumns,
          borderLeft: decoratedRecord._accountColor
            ? `4px solid ${decoratedRecord._accountColor}`
            : "4px solid transparent",
          borderRight: decoratedRecord._productColor
            ? `4px solid ${decoratedRecord._productColor}`
            : "4px solid transparent",
          borderTop: borderWidth
            ? `${borderWidth}px solid ${data.theme.palette.divider}`
            : undefined,
        }}
      >
        {data.columns.map((col) => (
          <div
            key={`${record.id}-${col.key}`}
            className={data.classes.virtualCell}
            style={{
              justifyContent: col.align === "center" ? "center" : "flex-start",
            }}
          >
            {col.render(decoratedRecord)}
          </div>
        ))}
      </div>
    </div>
  );
}, areEqual);
VirtualizedRow.displayName = "VirtualizedRow";
